import { useEffect, useRef, useState } from 'react';
import { councilWsClient } from '../services/councilWsClient';

/**
 * TH-1/TH-4 — "the crew is arriving…" chrome. Shows when the crew narrates its
 * CREW_GREETED event. TH-4: the greeting is now a CHORUS of N reads, so the
 * chrome stays up until ALL reads have landed (gate on speaker_ids.length),
 * dismissing on peer_turn_failed (R4) or a safety timeout so a silently-stalled
 * read can't pin it on forever. Stays subscribed for the session's lifetime.
 */
export function useCrewArriving(sessionId: string | undefined): boolean {
  const [active, setActive] = useState(false);
  const expected = useRef(1);
  const seen = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!sessionId) return;
    expected.current = 1;
    seen.current = 0;
    const clearTimer = () => {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    };
    const dismiss = () => { clearTimer(); setActive(false); };
    const unsub = councilWsClient.subscribe(sessionId, (frame: any) => {
      const et = frame?.event_type;
      if (et === 'crew_greeted') {
        expected.current = Math.max(1, frame?.target?.speaker_ids?.length || 1);
        seen.current = 0;
        setActive(true);
        clearTimer();
        timer.current = setTimeout(dismiss, 15000);   // R4 safety net for a lost read
      } else if (et === 'peer_turn_failed') {
        dismiss();
      } else if (et === 'turn_added') {
        seen.current += 1;
        if (seen.current >= expected.current) dismiss();
      }
    });
    return () => { clearTimer(); unsub(); };
  }, [sessionId]);
  return active;
}
